import { SCROLL_AXES, type ScrollAxis } from "./scroll-box-axes"
import { collectContentWithin, contentSpans, innerBoxSpan, isShownToUsers } from "./scroll-box-content-leaves"
import type { DetectedFinding, OutsideWindowFinding, OutsideWindowReason, ScrollEdge } from "./scroll-box-findings"
import { identifyScrollBox } from "./scroll-box-identity"
import {
  endOverhangPx,
  intersectSpans,
  overhangPx,
  rectSpan,
  scrollportSpan,
  spanExtentPx,
  spansOverlap,
  startOverhangPx,
  windowSpan,
  type PixelSpan,
} from "./scroll-box-spans"
import {
  OVERFLOW_THRESHOLD_PX,
  clipsAlong,
  clipsOrScrollsAlong,
  hasOwnText,
  isFixedPositioned,
  pageClipsAlong,
  scrollsAlong,
} from "./scroll-box-styles"

const SCROLL_EDGES: readonly ScrollEdge[] = ["start", "end"]

interface PlacedContent {
  element: Element
  span: PixelSpan
}

interface OutsideContent {
  spillRoot: Element
  axis: ScrollAxis
  edge: ScrollEdge
  outsidePx: number
}

function escapesIntoAnotherLayer(element: Element): boolean {
  return isFixedPositioned(element)
}

function keepsItsContentWithin(bounds: PixelSpan, axis: ScrollAxis): (element: Element) => boolean {
  return (element) =>
    clipsOrScrollsAlong(element, axis) &&
    overhangPx(rectSpan(element.getBoundingClientRect(), axis), bounds) <= OVERFLOW_THRESHOLD_PX
}

function placedContentWithin(container: Element, axis: ScrollAxis, bounds: PixelSpan): PlacedContent[] {
  const content = collectContentWithin(container, axis, escapesIntoAnotherLayer, keepsItsContentWithin(bounds, axis))
  const ownText = hasOwnText(container) ? contentSpans(container, axis).map((span) => ({ element: container, span })) : []
  const leaves = content.leaves.flatMap((leaf) => contentSpans(leaf, axis).map((span) => ({ element: leaf, span })))
  const clippingBoxes = content.innerBoxes
    .filter((innerBox) => !scrollsAlong(innerBox, axis))
    .map((innerBox) => ({ element: innerBox, span: innerBoxSpan(innerBox, axis) }))
  return [...ownText, ...leaves, ...clippingBoxes]
}

function overhangPastEdge(span: PixelSpan, bounds: PixelSpan, edge: ScrollEdge): number {
  return edge === "end" ? endOverhangPx(span, bounds) : startOverhangPx(span, bounds)
}

function isPartlyInsideWindow(element: Element): boolean {
  const rect = element.getBoundingClientRect()
  return SCROLL_AXES.every((axis) => spansOverlap(rectSpan(rect, axis), windowSpan(axis)))
}

function outsideWindowFinding(
  element: Element,
  axis: ScrollAxis,
  edge: ScrollEdge,
  reason: OutsideWindowReason,
  outsidePx: number,
): OutsideWindowFinding {
  return {
    kind: "outside-window",
    identity: identifyScrollBox(element),
    boxWidthPx: element.clientWidth,
    boxHeightPx: element.clientHeight,
    axis: axis.name,
    edge,
    reason,
    outsidePx,
  }
}

function spanInsideLayer(span: PixelSpan, layer: Element, axis: ScrollAxis): PixelSpan | null {
  if (!clipsAlong(layer, axis)) return span
  const insideLayer = intersectSpans(span, scrollportSpan(layer, axis))
  return spanExtentPx(insideLayer) > 0 ? insideLayer : null
}

export function detectContentOutsideWindowInLayer(layer: Element): OutsideWindowFinding[] {
  if (!isFixedPositioned(layer) || !isShownToUsers(layer) || !isPartlyInsideWindow(layer)) return []
  return SCROLL_AXES.filter((axis) => !scrollsAlong(layer, axis)).flatMap((axis) => {
    const spans = placedContentWithin(layer, axis, windowSpan(axis)).flatMap(
      (placed) => spanInsideLayer(placed.span, layer, axis) ?? [],
    )
    return SCROLL_EDGES.flatMap((edge) => {
      const outsidePx = Math.max(0, ...spans.map((span) => overhangPastEdge(span, windowSpan(axis), edge)))
      return outsidePx > OVERFLOW_THRESHOLD_PX ? [outsideWindowFinding(layer, axis, edge, "fixed-layer", outsidePx)] : []
    })
  })
}

function pageScrollOffsetPx(axis: ScrollAxis): number {
  return axis.name === "vertical" ? window.scrollY : window.scrollX
}

function reachablePageSpan(axis: ScrollAxis): PixelSpan {
  return pageClipsAlong(axis) ? windowSpan(axis) : { startPx: -pageScrollOffsetPx(axis), endPx: Number.POSITIVE_INFINITY }
}

function straddlesEdge(element: Element, axis: ScrollAxis, bounds: PixelSpan, edge: ScrollEdge): boolean {
  const span = rectSpan(element.getBoundingClientRect(), axis)
  return edge === "end"
    ? span.startPx < bounds.endPx && span.endPx > bounds.endPx + OVERFLOW_THRESHOLD_PX
    : span.endPx > bounds.startPx && span.startPx < bounds.startPx - OVERFLOW_THRESHOLD_PX
}

function spillRootOf(element: Element, axis: ScrollAxis, bounds: PixelSpan, edge: ScrollEdge): Element | null {
  for (let candidate: Element | null = element; candidate && candidate !== document.body; candidate = candidate.parentElement) {
    if (straddlesEdge(candidate, axis, bounds, edge)) return candidate
  }
  return null
}

function outsideContentOfPage(axis: ScrollAxis): OutsideContent[] {
  const bounds = reachablePageSpan(axis)
  return placedContentWithin(document.body, axis, bounds).flatMap((placed) =>
    SCROLL_EDGES.flatMap((edge) => {
      const outsidePx = overhangPastEdge(placed.span, bounds, edge)
      if (outsidePx <= OVERFLOW_THRESHOLD_PX) return []
      const spillRoot = spillRootOf(placed.element, axis, bounds, edge)
      return spillRoot ? [{ spillRoot, axis, edge, outsidePx }] : []
    }),
  )
}

function pageReason(axis: ScrollAxis): OutsideWindowReason {
  return pageClipsAlong(axis) ? "page-clips" : "before-page-start"
}

function largestPerSpillRootAndEdge(outsideContent: OutsideContent[]): OutsideContent[] {
  const largest: OutsideContent[] = []
  for (const content of outsideContent) {
    const keptIndex = largest.findIndex((kept) => kept.spillRoot === content.spillRoot && kept.edge === content.edge)
    if (keptIndex === -1) largest.push(content)
    else if (largest[keptIndex].outsidePx < content.outsidePx) largest[keptIndex] = content
  }
  return largest
}

export function detectPageContentOutsideWindow(): DetectedFinding[] {
  return SCROLL_AXES.flatMap((axis) =>
    largestPerSpillRootAndEdge(outsideContentOfPage(axis)).map((content) => ({
      element: content.spillRoot,
      finding: outsideWindowFinding(content.spillRoot, axis, content.edge, pageReason(axis), content.outsidePx),
    })),
  )
}
